import {Box, IconButton, List, ListItem, ListItemText, MenuItem, TextField, Toolbar, Typography} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {Add, Close, Label, LabelOutlined} from "@mui/icons-material";
import {connect} from "react-redux";
import {IGraphData, UpdateModeData, updateModeData} from "../../../redux/slices/editor";
import React, {ChangeEventHandler, MouseEventHandler, useContext, useEffect, useState} from "react";
import {selectModeData, selectProject} from "../../../redux/selectors/editor";
import {selectConstants, selectNetwork, selectTags} from "../../../redux/selectors/firebase";
import {NetworkData} from "../../../fstn/network";
import {Constants, ITag, Tags} from "../StateTags";
import {FirebaseContext} from "../../firebase/FirebaseProvider";
import AddTagDialog from "../../tags/AddTagDialog";

const useStyles = makeStyles((theme) => ({
    list: {
        overflowX: "hidden",
        overflowY: "auto",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    },
    stateList: {
        maxHeight: 136,
    },
    toolbar: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.background.paper,
        marginTop: theme.spacing(1),
        minHeight: 0,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1)
    },
    toolbarSelectInput: {
        paddingBottom: 0,
    },
    toolbarSelectRoot: {
        color: theme.palette.background.paper,
        verticalAlign: "middle",
        "& .MuiSvgIcon-root": {
            color: theme.palette.background.paper
        },
        "& .MuiInput-root:before": {
            borderWidth: 0
        },
        "& .MuiInput-root:after": {
            borderWidth: 0
        },
        "& .MuiInput-root": {
            color: theme.palette.background.paper
        }
    }
}));

interface IProps {
    constants: Constants
    graphData: IGraphData
    network: NetworkData
    project: string
    states: string[]
    tags: Tags
    updateModeData: UpdateModeData
}
function SelectedDetails({constants, graphData, network, project, states, tags, updateModeData}: IProps) {
    const classes = useStyles();
    const [addTagDialog, setAddTagDialog] = useState(false);
    const [addTagKey, setAddTagKey] = useState("");
    const [showName, setShowName] = useState(false);
    const {set} = useContext(FirebaseContext);
    const handleAddTagChange: ChangeEventHandler<HTMLInputElement> = ({target}) => {
        setAddTagKey(target.value);
        localStorage.setItem("selectedDetails.addTagKey", target.value);
    };
    const handleAddTag = () => {
        setAddTagDialog(true);
    };
    const handleClearTags: MouseEventHandler<HTMLButtonElement> = async ({currentTarget}) => {
        if (currentTarget.dataset.states && currentTarget.dataset.tag) {
            const states = currentTarget.dataset.states.split(",");
            const [type, value] = currentTarget.dataset.tag.split(":");

            for (let state of states) {
                let stateTags: {[key: string]: ITag } = {};

                Object.keys(tags[state])
                    .filter(key => tags[state][key].type !== type || tags[state][key].value.toString() !== value)
                    .forEach(key => {
                        stateTags[key] = {...tags[state][key]}
                    })
                await set(`tags/${project}/${state}`, stateTags).catch(console.error);
            }
        }
    };
    const handleCloseSelected: MouseEventHandler<HTMLButtonElement> = ({currentTarget}) => {
        updateModeData({
            ...graphData,
            selected: graphData.selected.filter(state => state !== currentTarget.dataset.state)
        });
    };
    const handleNameToggle = () => setShowName(!showName);
    let stateTags: {[tag: string]: string[]} = {};

    useEffect(() => {
        const constantKeys = Object.keys(constants || {});

        if (constantKeys.length) {
            const addTagKey = localStorage.getItem("selectedDetails.addTagKey") || "";

            if (constants[addTagKey]) {
                setAddTagKey(addTagKey);
            } else {
                setAddTagKey(constantKeys[0]);
            }
        }
    }, [constants]);
    states.forEach(state => 
        Object.keys(tags[state] || {}).forEach(tag => {
            const type = tags[state][tag].type;
            const name = `${type}:${tags[state][tag].value}:${constants[type].type}`;

            if (stateTags[name] === undefined) stateTags[name] = [];
            stateTags[name].push(state);
        }));
    return (
        <Box display="flex" flexDirection="column">
            {addTagDialog && addTagKey &&
                <AddTagDialog type={addTagKey} selection={graphData.selected} onClose={
                    () => setAddTagDialog(false)} />}
            <Toolbar classes={{dense: classes.toolbar}} disableGutters variant="dense">
                <Typography flexGrow={1} variant="h6">Selected</Typography>
                <IconButton color="inherit" edge="end" onClick={handleNameToggle} size="small" title="Toggle names">
                    {showName ? <Label/> : <LabelOutlined/>}
                </IconButton>
            </Toolbar>
            <Box className={[classes.list, classes.stateList].join(" ")}>
                {states.map(key => {
                    const state = network.states[key];

                    return (
                        <Box display="flex" key={key}>
                            <IconButton data-state={key} onClick={handleCloseSelected} size="small"
                                        title={"Unselect state"}>
                                <Close/>
                            </IconButton>
                            <ListItemText primary={showName ? state?.name : key}/>
                        </Box>
                    );
                })}
            </Box>
            <Toolbar classes={{dense: classes.toolbar}} disableGutters variant="dense">
                <Typography flexGrow={1} variant="h6">Blocks</Typography>
            </Toolbar>
            <Box className={classes.list}>
                <List>
                    {states.filter(state => network.states[state].meta?.blockNames).map(state =>
                        <Box key={state}>
                            {network.states[state].meta!.blockNames!.map(blockName =>
                                <ListItem key={blockName}>
                                    <ListItemText primary={state} secondary={blockName}/>
                                </ListItem>
                            )}
                        </Box>
                    )}
                </List>
            </Box>
            <Toolbar classes={{dense: classes.toolbar}} disableGutters variant="dense">
                <Typography flexGrow={1} variant="h6">Tags</Typography>
                {addTagKey &&
                    <Box>
                        <TextField classes={{root: classes.toolbarSelectRoot}}
                                   inputProps={{className: classes.toolbarSelectInput}}
                                   onChange={handleAddTagChange} select
                                   size="small" value={addTagKey} variant="standard">
                            <MenuItem disabled value=""><em>Select Tag</em></MenuItem>
                            {Object.keys(constants).map(key =>
                                <MenuItem key={key} value={key}>
                                    {constants[key].name}
                                </MenuItem>
                            )}
                        </TextField>
                        <IconButton color="inherit" edge="end" onClick={handleAddTag} size="small"
                            title="Add tag to selected">
                            {<Add/>}
                        </IconButton>
                    </Box>
                }
            </Toolbar>
            <Box className={[classes.list].join(" ")}>
                {Object.keys(stateTags)
                    .sort((lhs, rhs) => lhs.localeCompare(rhs))
                    .map(key => {
                        const [tag, value, type] = key.split(":");
                        const tagValue = type === "date" ? new Date(Number(value)).toLocaleString() : value;

                        return (
                            <Box display="flex" key={key}>
                                <IconButton data-tag={key} data-states={stateTags[key]} onClick={handleClearTags}
                                            size="small" title="Remove tag from selected states">
                                    <Close/>
                                </IconButton>
                                <ListItemText primary={`${tag}: ${tagValue} (${stateTags[key].length})`}/>
                            </Box>
                        );
                    })}
            </Box>
        </Box>
    );
}
const mapStateToProps = (store: never) => ({
    constants: selectConstants(store) ?? {},
    graphData: selectModeData(store) as IGraphData,
    network: selectNetwork(store),
    project: selectProject(store),
    tags: selectTags(store) ?? {}
});

export default connect(mapStateToProps, {
    updateModeData
})(SelectedDetails);
